import React, { useState, useEffect } from "react"

import { CollectionMenu } from "@app/scripts/components/collection-menu"
import { CollectionFilterModel } from "@app/scripts/components/collection-filter-model"

import { session } from "@app/scripts/lib/session"
import { stripHTML } from "@app/scripts/lib/utils"

export type Collection = {
  title?: string
  body_html?: string
}

export type CollectionBannerMenuItem = {
  title: string
  link: string
  active?: boolean
}

type collectionData = {
  collection: Collection
  collectionBannerMenu?: CollectionBannerMenuItem[]
}

export type Props = {
  collectionData: collectionData
}

export function CollectionBanner({ collectionData }: Props) {
  const [showFullDescription, setShowFullDescription] = useState(false)
  const [isModelView, setIsModelView] = useState(true)
  const [showFixedBanner, setShowFixedBanner] = useState(false)
  const fixedBannerOffset =
    "calc(var(--header-sticky-top, 0px) + var(--header-height, 0px))"

  const { collection, collectionBannerMenu } = collectionData

  const fullDescription = stripHTML(collection?.body_html || "")
  const truncatedDescription =
    fullDescription?.length > 200
      ? fullDescription.slice(0, 200).trim() + "..."
      : fullDescription

  // Read image type view session value and update model toggle
  useEffect(() => {
    setIsModelView(session.imageTypeView.value || true)
  }, [])

  // Handles Model toggle updates
  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setIsModelView(e.target.checked || false)
  }

  // Hide skeleton banner on collection load
  useEffect(() => {
    const skeleton = document.querySelector(
      ".collection-banner--skeleton-container"
    )
    if (skeleton) {
      skeleton.classList.remove("is-visible")
      skeleton.classList.add("is-hidden")
    }
  }, [collection])

  // Scroll listener to toggle fixed collection banner
  useEffect(() => {
    let animationFrame = 0

    const getFixedBannerTop = () => {
      const headerSection = document.querySelector(".header-section")

      if (headerSection) {
        return headerSection.getBoundingClientRect().bottom
      }

      const rootStyles = window.getComputedStyle(document.documentElement)
      const headerStickyTop =
        parseFloat(rootStyles.getPropertyValue("--header-sticky-top")) || 0
      const headerHeight =
        parseFloat(rootStyles.getPropertyValue("--header-height")) || 0

      return headerStickyTop + headerHeight
    }

    const updateFixedBanner = () => {
      window.cancelAnimationFrame(animationFrame)
      animationFrame = window.requestAnimationFrame(() => {
        const filterControls = document.querySelector(
          ".collection-banner--container .collection-banner--filter-model-container"
        )

        if (!filterControls) {
          setShowFixedBanner(false)
          return
        }

        const shouldShowFixedBanner =
          filterControls.getBoundingClientRect().top <= getFixedBannerTop()

        setShowFixedBanner((isVisible) =>
          isVisible === shouldShowFixedBanner
            ? isVisible
            : shouldShowFixedBanner
        )
      })
    }

    updateFixedBanner()
    window.addEventListener("scroll", updateFixedBanner, { passive: true })
    window.addEventListener("resize", updateFixedBanner)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      window.removeEventListener("scroll", updateFixedBanner)
      window.removeEventListener("resize", updateFixedBanner)
    }
  }, [collection])

  return (
    <>
      {/* Fixed Banner - shown on scroll */}
      {showFixedBanner && (
        <div
          className="collection-banner--fixed-top"
          style={{ top: fixedBannerOffset }}
        >
          <div className="flex-container align-middle">
            {collection.title && (
              <>
                <h1 className="collection-banner--title h3 is-hidden md:is-visible">
                  {collection?.title}
                </h1>

                {collectionBannerMenu?.length > 0 && (
                  <div className="item-divider is-hidden md:is-visible"></div>
                )}
              </>
            )}
            {collectionBannerMenu?.length > 0 && (
              <div className="collection-banner--menu-link-container is-hidden md:is-visible-flex">
                <CollectionMenu collectionMenuItems={collectionBannerMenu} />
              </div>
            )}
          </div>

          <CollectionFilterModel
            isModelView={isModelView}
            handleToggleChange={handleToggleChange}
          />
        </div>
      )}

      {/* Main Banner Content - shown above grid */}
      <div className="collection-banner--container">
        <div className="collection-banner--info-container">
          <div className="collection-banner--title-menu">
            {collection.title && (
              <>
                <h1 className="collection-banner--title h3">
                  {collection?.title}
                </h1>

                {collectionBannerMenu?.length > 0 && (
                  <div className="item-divider is-hidden md:is-visible"></div>
                )}
              </>
            )}
            {collectionBannerMenu?.length > 0 && (
              <div className="collection-banner--menu-link-container">
                <CollectionMenu collectionMenuItems={collectionBannerMenu} />
              </div>
            )}
          </div>

          {Boolean(fullDescription?.trim()) &&
            Boolean(truncatedDescription?.trim()) && (
              <div className="collection-banner--description">
                {showFullDescription ? fullDescription : truncatedDescription}
                {!showFullDescription && fullDescription?.length > 200 && (
                  <button
                    onClick={() => setShowFullDescription(true)}
                    className="collection-banner--description-read-more-button underline"
                  >
                    Read More
                  </button>
                )}
              </div>
            )}
        </div>

        <div className="collection-banner--filter-model-container">
          <CollectionFilterModel
            isModelView={isModelView}
            handleToggleChange={handleToggleChange}
          />
        </div>
      </div>
    </>
  )
}
